/**
 * The WebGL2 mockup preview's four shaders, as source strings so `gl.ts` stays about GL objects and
 * this file is the one place the maths lives.
 *
 * ORIENTATION (one rule, everywhere): every texture is stored EXACTLY as its array or bitmap arrives
 * — row 0 is the top of the image — and every pass that reads a texture indexes it at
 * `ivec2(gl_FragCoord.xy)` with NO flip, so each ping-pong FBO also comes out with texel row 0 = the
 * top of the image. Nothing is ever flipped twice, `readPixels` on the last FBO is top-down as it
 * stands, and the ONLY flip in the pipeline is `FRAG_BLIT`'s, which exists because the default
 * framebuffer's row 0 is the BOTTOM of the canvas as the browser displays it.
 *
 * PARITY: the print pass reproduces `renderMockup`'s per-pixel loop (packages/engine/src/mockup/
 * composite.ts) and the base pass reproduces `recolorGarment`/`recolorPixelF` (recolor.ts), in the
 * CPU's order, so that Task 4's GPU-vs-CPU comparison can hold to 2/255. The only intentional
 * differences are the ones the storage formats force: gx/gy arrive as RG8_SNORM (already ÷ 127, so
 * `uDispPx` carries `wrinkle × previewWidth` undivided) and the residual as RGB8_SNORM (÷ 127, so
 * the shader multiplies by 127/255 to land on the CPU's residual/255).
 */

/** One full-screen triangle with no buffers at all: (−1,−1), (3,−1), (−1,3) covers the clip square. */
export const VERT = `#version 300 es
void main() {
  gl_Position = vec4(gl_VertexID == 1 ? 3.0 : -1.0, gl_VertexID == 2 ? 3.0 : -1.0, 0.0, 1.0);
}
`;

/**
 * Pass 0 — the garment, and then the backdrop behind it. Copies the photo, and where `uRecolor` is
 * on blends in §5.7's recolour by the mask: the photo's own shading scaled towards the target's
 * luminosity (multiplicatively when the target is darker than the blank, by a screen when it is
 * lighter), then given the target's hue/chroma through W3C SetLum. `uGarment`/`uTarget` are 0..255
 * so the luminosities are divided exactly where `recolorPixelF` divides them.
 *
 * BACKDROP (`uBackdrop != 0`) mirrors `applyBackdrop` (packages/engine/src/mockup/backdrop.ts) at
 * the point `renderMockup` calls it — over the recoloured base, BEFORE any print — with
 * `out = b·s·(1 − m) + base·m`, `s = 1 − uShadowK·shadow`. Three details keep it to the byte:
 *
 *  - the recolour is ROUNDED to 8 bits first, because `recolorGarment` rounds once and the CPU's
 *    `applyBackdrop` then mixes that already-rounded base. Without it the two mixes would be fed
 *    values up to half a level apart, which the feathered mask edge would show;
 *  - `mix(b * s, col, m)` leaves a solid-garment pixel (`m == 1`) bit-exact, which is the CPU's
 *    `if (mv === 255) continue` — `mix` is `x·(1−a) + y·a`, so a = 1 is `y` exactly;
 *  - the final rounding is the framebuffer's own: every target here is RGBA8, and GL rounds to
 *    nearest on the write, exactly as `Math.round` does in the loop.
 *
 * Colours arrive in 0..1 (`gl-job.ts` divides by 255 once per frame rather than per pixel), the
 * gradient runs top to bottom over `uHeight` (`max(uHeight - 1, 1)` so a 1-px photo reads all top,
 * as `backdropAt` does), and the scene is fetched pixel for pixel from a cover cut to exactly this
 * size — never sampled — which is what makes it the same image the CPU read.
 */
export const FRAG_BASE = `#version 300 es
precision highp float;
precision highp int;
uniform highp sampler2D uPhoto;
uniform highp sampler2D uMask;
uniform highp sampler2D uShadow;
uniform highp sampler2D uScene;
uniform vec3 uGarment;
uniform vec3 uTarget;
uniform bool uRecolor;
uniform int uBackdrop;
uniform vec3 uBackA;
uniform vec3 uBackB;
uniform float uShadowK;
uniform int uHeight;
out vec4 o;

float lum3(vec3 c) { return 0.3 * c.r + 0.59 * c.g + 0.11 * c.b; }

// W3C SetLum's ClipColor. l/n/x all come from the INCOMING colour (as on the CPU): the x > 1
// correction is applied to whatever the n < 0 one left, but tests the original extremes.
vec3 clipColor(vec3 c) {
  float l = lum3(c), n = min(min(c.r, c.g), c.b), x = max(max(c.r, c.g), c.b);
  vec3 v = c;
  if (n < 0.0) {
    float den = l - n;
    v = abs(den) < 1e-9 ? clamp(v, 0.0, 1.0) : l + ((v - l) * l) / den;
  }
  if (x > 1.0) {
    float den = x - l;
    v = abs(den) < 1e-9 ? clamp(v, 0.0, 1.0) : l + ((v - l) * (1.0 - l)) / den;
  }
  return v;
}
vec3 setLum(vec3 c, float l) { return clipColor(c + (clamp(l, 0.0, 1.0) - lum3(c))); }

void main() {
  ivec2 p = ivec2(int(gl_FragCoord.x), int(gl_FragCoord.y));
  vec3 src = texelFetch(uPhoto, p, 0).rgb;
  float m = texelFetch(uMask, p, 0).r;
  vec3 col = src;
  if (uRecolor && m > 0.0) {
    float lp = lum3(src), lb = lum3(uGarment) / 255.0;
    // A pure black or white target would scale every fold to one level; clamping the TARGET's
    // luminosity to 2-98 % keeps the scaling non-degenerate, exactly as the CPU does.
    float lg = clamp(lum3(uTarget) / 255.0, 0.02, 0.98);
    float l;
    if (lg <= lb) l = lb > 1e-6 ? (lp * lg) / lb : lg;
    else l = lb < 1.0 - 1e-6 ? 1.0 - ((1.0 - lp) * (1.0 - lg)) / (1.0 - lb) : lg;
    col = mix(src, setLum(uTarget / 255.0, clamp(l, 0.0, 1.0)), m);
    // recolorGarment rounds its blend to 8 bits once, and that rounded value is what the CPU's
    // backdrop mix below is fed. Off the recolour path col is the photo's own texel, which is
    // already exactly an 8-bit level, so this is the only place the rounding is needed.
    col = floor(col * 255.0 + 0.5) / 255.0;
  }
  if (uBackdrop != 0) {
    vec3 b = uBackdrop == 1 ? uBackA
      : uBackdrop == 2 ? mix(uBackA, uBackB, float(p.y) / float(max(uHeight - 1, 1)))
      : texelFetch(uScene, p, 0).rgb;
    float s = 1.0 - uShadowK * texelFetch(uShadow, p, 0).r;
    col = mix(b * s, col, m);
  }
  o = vec4(col, 1.0);
}
`;

/**
 * Pass i — one print over whatever the previous pass left in `uBase`, so overlapping areas layer in
 * the order they are drawn (as on the CPU). Per pixel: displace by the fold gradient, invert the
 * area's homography, turn by the print's inverse rotation about the art rect's centre, sample the
 * art bilinearly in premultiplied alpha, shade by the blurred luma, add the fabric residual, and
 * blend by `coverage × look opacity × mask`.
 */
export const FRAG_PRINT = `#version 300 es
precision highp float;
precision highp int;
uniform highp sampler2D uBase, uMask, uLuma, uGrad, uResidual, uArt;
uniform mat3 uHinv;
uniform vec2 uAreaIn;
uniform vec4 uRect;
uniform vec2 uRot;
uniform vec2 uArtSize;
uniform vec4 uLook;
uniform float uLmid, uDispPx;
out vec4 o;
void main() {
  ivec2 p = ivec2(int(gl_FragCoord.x), int(gl_FragCoord.y));
  vec4 base = texelFetch(uBase, p, 0); o = base;
  float m = texelFetch(uMask, p, 0).r; if (m <= 0.0) return;
  vec2 g = texelFetch(uGrad, p, 0).rg;
  vec2 q = vec2(p) + 0.5 + g * uDispPx;
  vec3 h = uHinv * vec3(q, 1.0); if (abs(h.z) < 1e-12) return;
  vec2 uv = h.xy / h.z; if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) return;
  vec2 inch = uv * uAreaIn; vec2 c = uRect.xy + uRect.zw * 0.5; vec2 d = inch - c;
  vec2 r = c + vec2(d.x * uRot.x + d.y * uRot.y, -d.x * uRot.y + d.y * uRot.x);
  vec2 a = (r - uRect.xy) / uRect.zw * uArtSize;
  if (a.x < 0.0 || a.y < 0.0 || a.x >= uArtSize.x || a.y >= uArtSize.y) return;
  vec2 x = a - 0.5; vec2 f = fract(x); ivec2 i0 = ivec2(floor(x)); ivec2 sz = ivec2(uArtSize);
  vec4 acc = vec4(0.0);
  for (int dy = 0; dy < 2; dy++) for (int dx = 0; dx < 2; dx++) {
    float w = (dx == 0 ? 1.0 - f.x : f.x) * (dy == 0 ? 1.0 - f.y : f.y); ivec2 t = i0 + ivec2(dx, dy);
    if (w > 0.0 && t.x >= 0 && t.y >= 0 && t.x < sz.x && t.y < sz.y) { vec4 s = texelFetch(uArt, t, 0); float pa = s.a * w; acc += vec4(s.rgb * pa, pa); }
  }
  if (acc.a <= 0.0) return;
  vec3 C = acc.rgb / acc.a; float ca = acc.a;
  float L = texelFetch(uLuma, p, 0).r; float shadow = min(L / uLmid, 1.0);
  float hi = clamp((L - uLmid) / max(1e-6, 1.0 - uLmid), 0.0, 1.0);
  float sf = 1.0 - uLook.x + uLook.x * shadow; float hf = 1.0 - hi * uLook.y;
  vec3 res = texelFetch(uResidual, p, 0).rgb * (127.0 / 255.0);
  vec3 C3 = clamp(1.0 - (1.0 - C * sf) * hf + res * uLook.z, 0.0, 1.0);
  float k = ca * uLook.w * m;
  o = vec4(mix(base.rgb, C3, k), 1.0);
}
`;

/** The final pass: the last FBO onto the canvas, flipped — the one and only flip (see the header). */
export const FRAG_BLIT = `#version 300 es
precision highp float;
precision highp int;
uniform highp sampler2D uSrc;
uniform int uHeight;
out vec4 o;
void main() {
  o = vec4(texelFetch(uSrc, ivec2(int(gl_FragCoord.x), uHeight - 1 - int(gl_FragCoord.y)), 0).rgb, 1.0);
}
`;
